import { Typography, Box, TextField, Button, Link, CircularProgress } from '@mui/material';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import apiReq from 'core/api';
import { fetchUserInfo } from '../../../redux/actions';
import { tokenStorage } from 'utils';

import styles from '../Auth.module.scss';
import { PASSWORD_MIN_LENGTH, validateEmail } from '../helpers';
import PasswordField from '../components/PasswordField';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('reason') === 'session_expired';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email обязателен';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Введите корректный email';
    }

    if (!formData.password) {
      newErrors.password = 'Пароль обязателен';
    } else if (formData.password.length < PASSWORD_MIN_LENGTH) {
      newErrors.password = `Пароль должен содержать минимум ${PASSWORD_MIN_LENGTH} символов`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);
      try {
        const body = {
          email: formData.email,
          password: formData.password,
        };

        const data = await apiReq('auth/login', 'post', body);

        tokenStorage.set(data.access_token);

        await dispatch(fetchUserInfo());

        navigate('/chat');
      } catch (error) {
        console.log(error);
        setErrors({ authError: error?.data?.message || 'Неверный логин или пароль' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <Box className={styles['auth-container']}>
      <Box className={styles['auth-card']}>
        <Box className={styles['auth-brand']}>
          <img
            className={styles['auth-brand-mark']}
            src={`${process.env.PUBLIC_URL}/logo.svg`}
            alt="Контекст"
            width={26}
            height={26}
          />
          <Box className={styles['auth-brand-name']}>Контекст</Box>
        </Box>
        <Typography className={styles['auth-title']}>Вход в систему</Typography>
        <Typography className={styles['auth-subtitle']}>
          Введите email и пароль, чтобы продолжить
        </Typography>

        {sessionExpired && (
          <Box className={styles['auth-notice']}>Сессия истекла. Пожалуйста, войдите снова.</Box>
        )}

        <Box component="form" onSubmit={handleSubmit} className={styles['auth-form']}>
          <TextField
            name="email"
            label="Email"
            type="email"
            size="small"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            className={styles['auth-input']}
            variant="outlined"
          />

          <PasswordField
            name="password"
            label="Пароль"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            helperText={errors.password}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            className={styles['auth-button']}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={18} color="inherit" /> : 'Войти'}
          </Button>

          {errors.authError && <Box className={styles['auth-error']}>{errors.authError}</Box>}
        </Box>

        <Link
          component="button"
          variant="body2"
          onClick={handleForgotPassword}
          className={styles['auth-link']}
        >
          Забыли пароль?
        </Link>
      </Box>
    </Box>
  );
};

export default Login;
